'use client'

import { useState, useCallback, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { v4 as uuidv4 } from 'uuid';
import styles from '../styles/CardPaymentFlow.module.css';
import { CardFlowEmailVerificationForm } from './CardFlowEmailVerificationForm';
import { ReferralCodeInput } from './ReferralCodeInput';
import { useAuth } from '../../context/AuthContext';

// Dynamically import MoonPayBuyWidget
const MoonPayBuyWidget = dynamic(
  () => import('@moonpay/moonpay-react').then((mod) => mod.MoonPayBuyWidget),
  { ssr: false }
);

// Define TOKEN_PRICE constant
const TOKEN_PRICE = 0.012; // USD per GAM token

interface CardPaymentFlowProps {
  isOpen: boolean;
  onClose: () => void;
  onBackToPaymentSelection: () => void;
  projectUsdtWalletAddress?: string;
  isMounted: boolean;
  onGetSignatureForMoonPay: (urlToSign: string) => Promise<string>;
  onMoonPayTransactionCompleted: (event: any) => Promise<void>;
  onMoonPayLogin: (props: any) => Promise<void>;
}

export const CardPaymentFlow = ({
  isOpen,
  onClose,
  onBackToPaymentSelection,
  projectUsdtWalletAddress,
  isMounted,
  onGetSignatureForMoonPay,
  onMoonPayTransactionCompleted,
  onMoonPayLogin,
}: CardPaymentFlowProps) => {
  const [cardFlowStep, setCardFlowStep] = useState(1);
  const [userEmail, setUserEmail] = useState('');
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [fiatAmount, setFiatAmount] = useState('50');
  const [isLoading, setIsLoading] = useState(false);
  const [uiError, setUiError] = useState<string | null>(null);
  const [currentExternalId, setCurrentExternalId] = useState<string | null>(null);
  const [showMoonPayWidget, setShowMoonPayWidget] = useState(false);
  const auth = useAuth();

  // Calculate estimated GAM tokens
  const estimatedGamTokens = useMemo(() => {
    const amount = parseFloat(fiatAmount);
    if (isNaN(amount) || amount <= 0 || !TOKEN_PRICE) return 0;
    return amount / TOKEN_PRICE;
  }, [fiatAmount]);

  useEffect(() => {
    if (!isOpen) {
        setCardFlowStep(1);
        setUserEmail('');
        setReferralCode(null);
        setShowMoonPayWidget(false);
        setCurrentExternalId(null);
        setUiError(null);
        setIsLoading(false);
        setFiatAmount('50');
    }
  }, [isOpen]);

  // Skip email verification if user is already signed in and verified
  useEffect(() => {
    if (isOpen && auth.user && auth.user.verifiedEmail) {
      setCardFlowStep(2); // Skip to referral code step
      setUserEmail(auth.user.email);
    }
  }, [isOpen, auth.user]);

  // Log props passed to MoonPayBuyWidget when it's visible and props change
  useEffect(() => {
    if (showMoonPayWidget) {
      console.log('[CardPaymentFlow] MoonPayBuyWidget relevant props:', {
        visible: showMoonPayWidget,
        baseCurrencyAmount: fiatAmount,
        // Ensure defaultCurrencyCode is stable or included if dynamic
        defaultCurrencyCode: "usdt_polygon", 
        walletAddress: projectUsdtWalletAddress,
        externalTransactionId: currentExternalId,
        email: userEmail,
      });
    }
  }, [showMoonPayWidget, fiatAmount, projectUsdtWalletAddress, currentExternalId, userEmail]); // Add all relevant props as dependencies

  const handleVerificationSuccess = useCallback((data: { userId: string; email: string; isNewUser: boolean }) => {
    console.log('CardPaymentFlow: Email verification successful', data);
    setUserEmail(data.email);
    setCardFlowStep(2);
    setUiError(null);
    auth.loginCardUser(data.email, data.userId);
  }, [auth]);

  const handleReferralCodeSubmit = useCallback((code: string | null) => {
    console.log('CardPaymentFlow: Referral code submitted:', code);
    setReferralCode(code);
    if (code) {
      localStorage.setItem('gam_referral_code', code);
    }
    setCardFlowStep(3);
    setUiError(null);
  }, []);

  const handleInitiateMoonPayPurchaseForCard = async () => {
    if (!projectUsdtWalletAddress) {
      setUiError("Payment provider is temporarily unavailable."); return;
    }
    const amount = parseFloat(fiatAmount);
    if (isNaN(amount) || amount <= 0) {
      setUiError('Please enter a valid amount.'); return;
    }
    setIsLoading(true);
    setUiError(null);
    const newExternalId = uuidv4();
    setCurrentExternalId(newExternalId);
    try {
      const response = await fetch('/api/moonpay/initiate-transaction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          externalTransactionId: newExternalId,
          userEmail: userEmail,
          fiatAmount: amount,
          currency: 'USD',
          referralCode: referralCode || undefined,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to initiate payment record.' }));
        throw new Error(errorData.error || `HTTP error! ${response.status}`);
      }
      setShowMoonPayWidget(true);
    } catch (err: any) {
      console.error('Failed to initiate MoonPay transaction for card user:', err);
      setUiError(err.message || 'Could not prepare payment.');
      setCurrentExternalId(null);
    }
    setIsLoading(false);
  };

  const handleMoonPayWidgetCloseInCardFlow = () => {
    setShowMoonPayWidget(false);
    setCurrentExternalId(null);
    if (!uiError) {
        setCardFlowStep(4);
    } else {
        setCardFlowStep(3);
    }
  };
  
  const getSignatureAndUpdateUi = useCallback(async (urlToSign: string): Promise<string> => {
    try {
      const signature = await onGetSignatureForMoonPay(urlToSign);
      return signature;
    } catch (error: any) {
      setUiError(`Payment session error: ${error.message}`);
      setShowMoonPayWidget(false);
      setCardFlowStep(3);
      throw error;
    }
  }, [onGetSignatureForMoonPay, setUiError, setShowMoonPayWidget, setCardFlowStep]);
  
  const handleTransactionCompletedAndUpdateUi = async (event: any) => {
    await onMoonPayTransactionCompleted(event);
    if (event.status === 'completed' || event.status === 'succeeded') {
      setUiError(null);
      setCardFlowStep(5);
    } else if (event.status === 'failed') {
      setUiError(`MoonPay transaction failed: ${event.error?.message || 'Unknown reason'}`);
      setCardFlowStep(3);
    }
  };

  if (!isMounted) return null;

  // Step indicator UI (matches crypto flow)
  const steps = [
    { label: 'Verify Email' },
    { label: 'Referral Code' },
    { label: 'Amount' },
    { label: 'Payment' },
    { label: 'Success' },
  ];

  return (
    <div>
      <div className={styles.steps}>
        {steps.map((step, idx) => {
          const stepNum = idx + 1;
          return (
            <div className={styles.step} key={step.label}>
              <div className={`${styles.stepCircle} ${cardFlowStep >= stepNum ? styles.active : ''} ${cardFlowStep > stepNum ? styles.completed : ''}`}>
                {cardFlowStep > stepNum ? '✓' : stepNum}
              </div>
              <div className={`${styles.stepLabel} ${cardFlowStep >= stepNum ? styles.active : ''} ${cardFlowStep > stepNum ? styles.completed : ''}`}>
                {step.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Main step content below */}
      {cardFlowStep === 1 && !showMoonPayWidget && !(auth.user && auth.user.verifiedEmail) && (
        <CardFlowEmailVerificationForm 
          onVerificationSuccess={handleVerificationSuccess}
          onBack={onBackToPaymentSelection}
        />
      )}
      {cardFlowStep === 2 && !showMoonPayWidget && (
        <ReferralCodeInput
          onPrevious={() => setCardFlowStep(1)}
          onNext={handleReferralCodeSubmit}
          initialValue={referralCode}
        />
      )}
      {cardFlowStep === 3 && !showMoonPayWidget && (
        <div className={styles.cardFlowStepContainer}>
          <button onClick={() => setCardFlowStep(2)} className={styles.backButton}>&larr; Back</button>
          <button onClick={onClose} className={styles.closeButton}>&times;</button>
          <h4 className={styles.header}>Buy GAM with Card</h4>
          <p className={styles.accountInfo}>Account: {userEmail}</p>
          <div className={styles.priceDisplayContainer}>
            <span className={styles.priceDisplayLabel}>GAM Token Price:</span>
            <span className={styles.priceDisplayValue}>${TOKEN_PRICE.toFixed(3)} USD</span>
          </div>
          <label htmlFor="fiatAmount" className={styles.inputLabel}>Amount (USD)</label>
          <input type="number" id="fiatAmount" value={fiatAmount} onChange={(e) => setFiatAmount(e.target.value)} placeholder="e.g., 50" className={styles.inputField} min="1" disabled={isLoading}/>
          {parseFloat(fiatAmount) > 0 && estimatedGamTokens > 0 && (
            <div className={styles.estimatedTokens}>
              You will receive approx: <strong>{estimatedGamTokens.toLocaleString(undefined, { maximumFractionDigits: 2 })} GAM</strong>
            </div>
          )}
          <button onClick={handleInitiateMoonPayPurchaseForCard} disabled={isLoading || !projectUsdtWalletAddress || parseFloat(fiatAmount) <= 0} className={styles.actionButton}>
            {isLoading ? 'Preparing Payment...' : 'Proceed to MoonPay'}
          </button>
          {uiError && <p className={styles.errorMessage}>{uiError}</p>}
          {referralCode && <p className={styles.accountInfo} style={{marginTop: '1rem', fontSize: '0.8rem'}}>Referral Code: {referralCode}</p>}
        </div>
      )}
      {cardFlowStep === 4 && !showMoonPayWidget && (
        <div className={styles.cardFlowStepContainer}>
          <button onClick={onClose} className={styles.closeButton}>&times;</button>
          <h4 className={styles.header}>Payment Status</h4>
          <p className={styles.confirmationText}>{uiError ? `Error: ${uiError}` : "Your GAM token purchase is processing! You'll be notified once confirmed."}</p>
          {referralCode && <p className={styles.accountInfo} style={{fontSize: '0.8rem'}}>Referral Code Used: {referralCode}</p>}
          <div className={styles.buttonGroup}>
            <button onClick={onClose} className={styles.actionButton}>Close</button>
            <button onClick={() => { setCardFlowStep(1); setUiError(null); setReferralCode(null);}} className={styles.secondaryButton}>Make another card payment</button>
          </div>
        </div>
      )}
      {cardFlowStep === 5 && !showMoonPayWidget && (
        <div className={styles.cardFlowStepContainer}>
          <button onClick={onClose} className={styles.closeButton}>&times;</button>
          <h4 className={styles.header}>Purchase Successful!</h4>
          <p className={styles.confirmationText}>Your GAM token purchase via MoonPay has been successfully processed.</p>
          <p className={styles.confirmationText}>Tokens will be distributed after the presale. You will be notified at {userEmail}.</p>
          {referralCode && <p className={styles.accountInfo} style={{fontSize: '0.8rem'}}>Referral Code Used: {referralCode}</p>}
          <div className={styles.buttonGroup}>
            <button onClick={onClose} className={styles.actionButton}>Close</button>
            <button onClick={() => { setCardFlowStep(1); setUiError(null); setReferralCode(null);}} className={styles.secondaryButton}>Make another card payment</button>
          </div>
        </div>
      )}
      {showMoonPayWidget && currentExternalId && projectUsdtWalletAddress && (
        <MoonPayBuyWidget
          variant="overlay"
          visible={showMoonPayWidget}
          baseCurrencyCode="usd"
          baseCurrencyAmount={fiatAmount}
          defaultCurrencyCode="usdc_base"
          currencyCode="usdc_base"
          walletAddress={projectUsdtWalletAddress}
          externalTransactionId={currentExternalId}
          onUrlSignatureRequested={getSignatureAndUpdateUi}
          onLogin={onMoonPayLogin}
          onCloseOverlay={handleMoonPayWidgetCloseInCardFlow}
          onTransactionCompleted={handleTransactionCompletedAndUpdateUi}
          email={userEmail}
        />
      )}
    </div>
  );
}; 